import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../services/api';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import {
  Search,
  Gavel as GavelIcon,
  Description as DocumentIcon,
  AccountBalance as CourtIcon,
  AutoStories as LawIcon,
  TrendingUp as TrendingIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import Sidebar from '../components/Sidebar';
import LawDetail from '../components/LawDetail';
import { searchLaws, getLawDetail, LawSearchResult, LawDetail as LawDetailType } from '../services/lawSearchService';
import FeatureLockModal from '../components/FeatureLockModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`research-tabpanel-${index}`}
      aria-labelledby={`research-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ResearchPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lawError, setLawError] = useState<string | null>(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Law search state
  const [lawResults, setLawResults] = useState<LawSearchResult[]>([]);
  const [lawLoading, setLawLoading] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState<LawDetailType | null>(null);
  const [openLawDetail, setOpenLawDetail] = useState(false);
  const [lawDetailLoading, setLawDetailLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string>('');
  // Filters for jurisprudence
  const [selectedCourts, setSelectedCourts] = useState<string[]>(['NSoud', 'VOsoud', 'TVsoud']);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const { collapsed } = useSidebar();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();

  // Dočasně uzamčeno: Právní výzkum
  const FEATURE_LOCKED = true;
  if (FEATURE_LOCKED) {
    return (
      <FeatureLockModal
        serviceName="Právní výzkum"
        description="Tato sekce je zatím v přípravě. Mezitím prosím vyzkoušejte jiné funkce, které jsou již dostupné."
        suggestions={[
          { label: 'Právní konzultace', to: '/consultation', variant: 'contained' },
          { label: 'Analýza smluv', to: '/contract-analysis', variant: 'outlined' }
        ]}
      />
    );
  }

  const scrollToSearchSection = () => {
    const searchSection = document.getElementById('search-section');
    if (searchSection) {
      searchSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // --- Highlight helpers (diacritics + simple Czech endings) ---
  const stripDiacritics = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}+/gu, '');
  const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const czCharClass = (ch: string) => {
    const map: Record<string, string> = { a:'aá', c:'cč', d:'dď', e:'eéě', i:'ií', n:'nň', o:'oó', r:'rř', s:'sš', t:'tť', u:'uúů', y:'yý', z:'zž' };
    const lower = ch.toLowerCase();
    const set = map[lower];
    return set ? `[${set}]` : escapeRegex(lower);
  };
  const normalizeBase = (term: string) => {
    const t = stripDiacritics(term.toLowerCase());
    const suffixes = ['ove','ami','emi','ach','ich','ych','eho','emu','iho','imu','ech','ost','nost','ace','aci','ou','em','om','um','am','im','y','a','u','e','i','o'];
    for (const suf of suffixes.sort((a,b)=>b.length-a.length)) {
      if (t.endsWith(suf) && t.length - suf.length >= 4) {
        return t.slice(0, t.length - suf.length);
      }
    }
    return t;
  };
  const termToPattern = (term: string) => {
    const base = normalizeBase(term);
    const chars = Array.from(base).map(czCharClass).join('');
    return `\\b${chars}(?:[a-zá-ž]{0,3})?\\b`;
  };
  const getHighlightTerms = (matched?: string[]) => {
    const list = (matched && matched.length > 0)
      ? matched
      : Array.from(new Set(stripDiacritics(searchQuery.toLowerCase())
          .split(/[^\p{L}\p{N}]+/u)
          .filter(t => t && t.length >= 3)
        )).slice(0,8);
    return list;
  };
  const highlightText = (text: string, matched?: string[]) => {
    if (!text) return text;
    const terms = getHighlightTerms(matched);
    if (terms.length === 0) return text;
    const pattern = terms.map(termToPattern).join('|');
    try {
      const re = new RegExp(`(${pattern})`, 'giu');
      const parts = text.split(re);
      return parts.map((part, idx) => (
        idx % 2 === 1 ? <mark key={idx}>{part}</mark> : <React.Fragment key={idx}>{part}</React.Fragment>
      ));
    } catch {
      return text;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setResults([]);
    setLawResults([]);
    setError(null);
    setLawError(null);
    setAiAnalysis('');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);
    setLawError(null);
    try {
      if (tabValue === 0) {
        // Real jurisprudence search with timeout
        const body: any = {
          query: searchQuery.trim(),
          limit: 20,
          court_types: selectedCourts
        };
        if (dateFrom) body.date_from = dateFrom;
        if (dateTo) body.date_to = dateTo;
        const response = await api.post(API_ENDPOINTS.jurisprudence.search, body, {
          timeout: 30000 // 30 second timeout
        });

        if (response.data && response.data.cases && response.data.cases.length > 0) {
          setResults(response.data.cases.map((caseItem: any) => ({
            id: caseItem.id,
            case_number: caseItem.case_number,
            court: caseItem.court_name,
            date: caseItem.decision_date || 'Neznámé datum',
            title: caseItem.case_title || '',
            summary: caseItem.case_summary || caseItem.case_title,
            relevance_score: caseItem.relevance_score || 0.8,
            matched_terms: caseItem.matched_terms || []
          })));
        } else {
          // If no real results, show that we found no results
          setResults([]);
          setError('Pro váš dotaz nebyly nalezeny žádné relevantní rozhodnutí. Zkuste jiné klíčové slova.');
        }
      } else if (tabValue === 1) {
        // Law search - check authentication first
        if (!isAuthenticated) {
          setLawError('Pro vyhledávání zákonů musíte být přihlášeni. Prosím přihlaste se.');
          return;
        }

        setLawLoading(true);
        setLawResults([]);
        const response = await searchLaws(searchQuery.trim(), 20);

        if (response.laws && response.laws.length > 0) {
          setLawResults(response.laws);
          setAiAnalysis(response.ai_query_analysis || '');
        } else {
          setLawResults([]);
          setLawError('Pro váš dotaz nebyly nalezeny žádné relevantní zákony. Zkuste jiné klíčové slova.');
        }
        setLawLoading(false);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
      setLawResults([]);
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        const errorMsg = 'Vyhledávání trvá příliš dlouho (více než 30 sekund). Zkuste to prosím později nebo použijte specifičtější klíčová slova.';
        if (tabValue === 0) {
          setError(errorMsg);
        } else {
          setLawError(errorMsg);
        }
      } else {
        const errorMsg = 'Nastala chyba při vyhledávání. Zkuste to prosím znovu.';
        if (tabValue === 0) {
          setError(errorMsg);
        } else {
          setLawError(errorMsg);
        }
      }
      if (tabValue === 1) {
        setLawLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCourtToggle = (code: string) => {
    setSelectedCourts(prev => (
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    ));
  };

  const handleCaseClick = async (caseItem: any) => {
    setSelectedCase(caseItem);
    setOpenDetail(true);
    setLoadingDetail(true);

    // For jurisprudence cases, fetch more details
    if (tabValue === 0 && caseItem.id) {
      try {
        const response = await api.get(
          API_ENDPOINTS.jurisprudence.getCase(caseItem.id),
          { timeout: 30000 }
        );

        if (response.data) {
          setSelectedCase({
            ...caseItem,
            // Merge case data from API
            ...response.data.case,
            fullDetail: {
              ai_analysis: response.data.ai_analysis,
              key_legal_points: response.data.key_legal_points,
              related_cases: response.data.related_cases,
              similar_cases_reasoning: response.data.similar_cases_reasoning
            }
          });
        }
      } catch (error) {
        console.error('Failed to load case details:', error);
      }
    }

    setLoadingDetail(false);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setSelectedCase(null);
  };

  const handleLawClick = async (law: LawSearchResult) => {
    setOpenLawDetail(true);
    setLawDetailLoading(true);

    try {
      const detail = await getLawDetail(law.cislo_zakona);
      setSelectedLaw(detail);
    } catch (err) {
      console.error('Failed to load law details:', err);
      setLawError('Nepodařilo se načíst detail zákona. Zkuste to prosím znovu.');
      setOpenLawDetail(false);
    } finally {
      setLawDetailLoading(false);
    }
  };

  const handleRelatedLawClick = async (lawNumber: string) => {
    setLawDetailLoading(true);

    try {
      const detail = await getLawDetail(lawNumber);
      setSelectedLaw(detail);
    } catch (err) {
      console.error('Failed to load related law details:', err);
      setLawError('Nepodařilo se načíst detail souvisejícího zákona. Zkuste to prosím znovu.');
    } finally {
      setLawDetailLoading(false);
    }
  };

  const handleCloseLawDetail = () => {
    setOpenLawDetail(false);
    setSelectedLaw(null);
  };


  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />

      <Box sx={{ 
        flex: 1,
        marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
        paddingTop: isMobile ? '64px' : 0,
        minHeight: '100vh',
        backgroundColor: '#F2F2F2',
        transition: 'margin-left 0.3s ease'
      }}>
        <Box sx={{ 
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 4, sm: 6 }
        }}>
        <Typography sx={{
          fontFamily: 'Inter',
          fontWeight: 700,
          fontSize: { xs: '24px', sm: '28px', md: '32px', lg: '36px' },
          lineHeight: { xs: 1.2, md: 1.17 },
          color: '#1F1F1F',
          mb: { xs: 2, md: 3 }
        }}>
          Právní výzkum
        </Typography>
      
        <Box sx={{
          backgroundColor: '#4C4C4C',
          color: '#DCDCDC',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0px 2px 2px rgba(18, 14, 13, 0.1)',
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <InfoIcon sx={{ width: 20, height: 20, color: '#DCDCDC', flexShrink: 0 }} />
          <Typography sx={{
            fontFamily: 'Inter',
            fontWeight: 500,
            fontSize: '12px',
            lineHeight: '18px',
            textAlign: 'center'
          }}>
            Profesionální upozornění: Prohledávejte databázi českých zákonů a soudních rozhodnutí. Výsledky slouží pouze pro informační účely.
          </Typography>
        </Box>

        {/* Jednoduchý návod – Právní výzkum (v aplikaci) */}
        {!loading && results.length === 0 && (
          <Box sx={{
            mt: { xs: 1, md: 2 },
            p: { xs: 2.5, sm: 3 },
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
            border: '1px solid rgba(0,0,0,0.06)',
            maxWidth: 900,
            mx: 'auto',
            mb: { xs: 4, sm: 5 }
          }}>
            <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '18px', sm: '20px' }, color: '#1F1F1F', textAlign: 'center', mb: 1.5 }}>
              Jak postupovat – Právní výzkum
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>1) Zadejte dotaz</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Napište, co hledáte (zákon, judikát, téma). Buďte co nejkonkrétnější.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>2) Prohlédněte výsledky</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Získáte shrnutí i odkazy na zdroje. Kliknutím otevřete detail.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>3) Uložte si, co potřebujete</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Vše je uloženo v Dashboardu – rychlý návrat kdykoliv.</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#10b981', mt: '2px' }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, color: '#1F1F1F', fontSize: { xs: '14px', sm: '15px' } }}>4) Historie v Dashboardu</Typography>
                  <Typography sx={{ color: '#555', fontSize: { xs: '13px', sm: '14px' } }}>Své dotazy a výsledky najdete vždy na jednom místě.</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Research Tools Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 4
        }}>
          <Card
            sx={{
              cursor: 'pointer',
              height: '100%',
              '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
              transition: 'all 0.3s ease',
              bgcolor: 'white',
              border: '3px solid #1F1F1F',
              borderRadius: '12px'
            }}
            onClick={() => {
              setTabValue(0);
              scrollToSearchSection();
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <GavelIcon sx={{ fontSize: 48, mb: 2, color: '#1F1F1F' }} />
              <Typography variant="h5" gutterBottom fontWeight={600} sx={{ color: '#1F1F1F' }}>
                Vyhledávání judikatury
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
                Inteligentní vyhledávání v rozhodnutích českých soudů
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="Nejvyšší soud" size="small" sx={{ bgcolor: 'rgba(31, 31, 31, 0.08)', color: '#1F1F1F', fontWeight: 500 }} />
                <Chip label="Vrchní soudy" size="small" sx={{ bgcolor: 'rgba(31, 31, 31, 0.08)', color: '#1F1F1F', fontWeight: 500 }} />
                <Chip label="Krajské soudy" size="small" sx={{ bgcolor: 'rgba(31, 31, 31, 0.08)', color: '#1F1F1F', fontWeight: 500 }} />
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  border: 'none !important',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
                endIcon={<GavelIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setTabValue(0);
                  scrollToSearchSection();
                }}
              >
                Začít vyhledávat
              </Button>
            </CardActions>
          </Card>

          <Card
            sx={{
              cursor: 'pointer',
              height: '100%',
              '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' },
              transition: 'all 0.3s ease',
              bgcolor: 'white',
              border: '3px solid #1F1F1F',
              borderRadius: '12px'
            }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <LawIcon sx={{ fontSize: 48, mb: 2, color: '#1F1F1F' }} />
                <Typography variant="h5" gutterBottom fontWeight={600} sx={{ color: '#1F1F1F' }}>
                  Databáze zákonů
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
                  Prohledávání právních předpisů a zákonů ČR
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label="Aktuální znění" size="small" sx={{ bgcolor: 'rgba(31, 31, 31, 0.08)', color: '#1F1F1F', fontWeight: 500 }} />
                  <Chip label="Sbírka zákonů" size="small" sx={{ bgcolor: 'rgba(31, 31, 31, 0.08)', color: '#1F1F1F', fontWeight: 500 }} />
                  <Chip label="EU právo" size="small" sx={{ bgcolor: 'rgba(31, 31, 31, 0.08)', color: '#1F1F1F', fontWeight: 500 }} />
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#1F1F1F !important',
                    backgroundColor: '#1F1F1F !important',
                    background: '#1F1F1F !important',
                    color: '#FFFFFF !important',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    border: 'none !important',
                    '&:hover': {
                      bgcolor: '#e72f0d !important',
                      backgroundColor: '#e72f0d !important',
                      background: '#e72f0d !important',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  endIcon={<LawIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTabValue(1);
                    scrollToSearchSection();
                  }}
                >
                  Prohlížet zákony
                </Button>
              </CardActions>
            </Card>
        </Box>

        {/* How it works section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 3, fontWeight: 600 }}>
            Jak to funguje
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
            gap: 3
          }}>
            <Card sx={{ textAlign: 'center', p: 3, '&:hover': { boxShadow: 4 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h2" sx={{ color: '#1F1F1F', fontWeight: 'bold', fontSize: '2rem' }}>
                  1
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Vyberte nástroj
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Klikněte na "Vyhledávání judikatury" pro soudní rozhodnutí nebo "Databáze zákonů" pro právní předpisy
              </Typography>
            </Card>

            <Card sx={{ textAlign: 'center', p: 3, '&:hover': { boxShadow: 4 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h2" sx={{ color: '#424242', fontWeight: 'bold', fontSize: '2rem' }}>
                  2
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Zadejte dotaz
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Napište klíčová slova nebo celou větu popisující váš právní problém nebo oblast zájmu
              </Typography>
            </Card>

            <Card sx={{ textAlign: 'center', p: 3, '&:hover': { boxShadow: 4 } }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h2" sx={{ color: '#1F1F1F', fontWeight: 'bold', fontSize: '2rem' }}>
                  3
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Prozkoumejte výsledky
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prohlížejte si relevantní rozhodnutí nebo právní ustanovení s AI analýzou a doporučeními
              </Typography>
            </Card>
          </Box>
        </Box>

        <Box id="search-section" sx={{
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.85) 100%)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '24px',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.07)',
          padding: { xs: '32px 24px', md: '48px 40px' },
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
            backgroundSize: '1000px 100%',
            animation: 'shimmer-search 3s infinite',
            pointerEvents: 'none',
            opacity: 0,
            transition: 'opacity 0.5s ease',
            zIndex: 0,
          },
          '&:hover::before': {
            opacity: 1,
          },
          '@keyframes shimmer-search': {
            '0%': {
              backgroundPosition: '-1000px 0',
            },
            '100%': {
              backgroundPosition: '1000px 0',
            },
          },
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                textAlign: 'center',
                mb: 4,
                fontWeight: 700,
                color: '#212121',
                fontSize: { xs: '1.8rem', md: '2.125rem' }
              }}
            >
              Rychlé vyhledávání
            </Typography>

            <Box sx={{
              display: 'flex',
              gap: { xs: 1, md: 2 },
              alignItems: 'stretch',
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <TextField
                fullWidth
                label="Hledaný výraz"
                placeholder="Zadejte klíčová slova pro vyhledávání..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.4)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      '& fieldset': {
                        borderColor: '#1F1F1F !important',
                        borderWidth: '2px',
                      }
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: '#666',
                    fontWeight: 500,
                    '&.Mui-focused': {
                      color: '#1F1F1F'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '16px 14px',
                    fontSize: '16px'
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : <Search />}
                onClick={handleSearch}
                disabled={loading || !searchQuery.trim()}
                sx={{
                  padding: { xs: '14px 28px', md: '14px 36px' },
                  bgcolor: '#1F1F1F !important',
                  backgroundColor: '#1F1F1F !important',
                  background: '#1F1F1F !important',
                  color: '#FFFFFF !important',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: { xs: '100%', sm: '140px' },
                  transition: 'all 0.3s ease',
                  border: 'none !important',
                  boxShadow: '0 4px 12px rgba(31, 31, 31, 0.15)',
                  '&:hover': {
                    bgcolor: '#e72f0d !important',
                    backgroundColor: '#e72f0d !important',
                    background: '#e72f0d !important',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(231, 47, 13, 0.25)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    opacity: 1,
                    cursor: 'not-allowed',
                    bgcolor: '#1F1F1F !important',
                    backgroundColor: '#1F1F1F !important',
                    background: '#1F1F1F !important',
                    color: 'rgba(255, 255, 255, 0.5) !important'
                  }
                }}
              >
                {loading ? 'Hledám...' : 'Vyhledat'}
              </Button>
            </Box>

            {/* Search Examples */}
            <Box sx={{ mt: 4, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#212121', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <InfoIcon sx={{ fontSize: 24, color: '#1F1F1F' }} />
                Příklady vyhledávání
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2
              }}>
                {[
                  'náhrada škody dopravní nehoda',
                  'výpověď z nájmu bez výpovědní lhůty',
                  'rozvod manželství společné jmění',
                  'pracovní smlouva neplatná výpověď',
                  'stavební povolení sousedské spory',
                  'dědictví vyloučení dědice'
                ].map((example, index) => (
                  <Box
                    key={index}
                    onClick={() => {
                      setSearchQuery(example);
                      // Automaticky spustit vyhledávání po kliknutí na příklad
                      setTimeout(() => handleSearch(), 100);
                    }}
                    sx={{
                      p: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      borderRadius: '12px',
                      border: '2px solid #1F1F1F',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: '#1F1F1F',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        '& .MuiTypography-root': {
                          color: '#FFFFFF',
                        }
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{
                      color: '#1F1F1F',
                      fontWeight: 600,
                      textAlign: 'center',
                      transition: 'color 0.3s ease'
                    }}>
                      "{example}"
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              color: '#666',
              fontWeight: 500,
              '&.Mui-selected': {
                color: '#1F1F1F',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1F1F1F'
            }
          }}
        >
          <Tab label="Soudní rozhodnutí" />
          <Tab label="Zákony a vyhlášky" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Filters */}
          <Paper sx={{ p: 2, pr: { md: 0 }, mb: 2, position: 'sticky', top: 8, zIndex: 2 }}>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              alignItems: 'flex-end',
              justifyContent: { xs: 'flex-start', md: 'space-between' },
              width: '100%'
            }}>
              <Box sx={{ flex: 1, pr: { md: 3 } }}>
                <Typography variant="subtitle2" gutterBottom>
                  Typ soudu ({selectedCourts.length}/3)
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(200px, 1fr))' },
                  gap: 1,
                  alignItems: 'center'
                }}>
                  {[
                    { code: 'NSoud', label: 'Nejvyšší soud' },
                    { code: 'VOsoud', label: 'Vrchní/Krajské (občanské)' },
                    { code: 'TVsoud', label: 'Vrchní/Krajské (trestní)' },
                  ].map(item => (
                    <FormControlLabel
                      key={item.code}
                      control={<Checkbox checked={selectedCourts.includes(item.code)} onChange={() => handleCourtToggle(item.code)} />}
                      label={item.label}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                gap: { xs: 1.5, md: 2 },
                alignItems: 'flex-end',
                ml: { md: 'auto' },
                pl: { md: 5 },
                borderLeft: { md: '1px solid #e0e0e0' },
                pr: 0,
                flexShrink: 0
              }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Od</Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: { xs: 140, md: 170 } }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Do</Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: { xs: 140, md: 170 } }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => { setSelectedCourts(['NSoud','VOsoud','TVsoud']); setDateFrom(''); setDateTo(''); }}
                >
                  Vymazat filtry
                </Button>
              </Box>
            </Box>

            {/* Přehled zvolených filtrů (dole, neodsouvá checkboxy) */}
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'nowrap', gap: 1, overflowX: 'auto', pb: 1 }}>
              <Chip label={`Soudy: ${selectedCourts.length}/3`} size="small" />
              {selectedCourts.map(code => (
                <Chip
                  key={code}
                  color="primary"
                  label={{ NSoud: 'Nejvyšší soud', VOsoud: 'Vrchní/Krajské (občanské)', TVsoud: 'Vrchní/Krajské (trestní)' }[code as 'NSoud'|'VOsoud'|'TVsoud'] || code}
                  onDelete={() => handleCourtToggle(code)}
                />
              ))}
              {(dateFrom || dateTo) && (
                <Chip
                  color="secondary"
                  label={`Datum: ${dateFrom || '—'} až ${dateTo || '—'}`}
                  onDelete={() => { setDateFrom(''); setDateTo(''); }}
                />
              )}
            </Box>

          </Paper>
          {error ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : null}
          {results.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Nalezeno {results.length} výsledků
              </Typography>
              {results.map((result) => (
                <Card
                  key={result.id}
                  onClick={() => handleCaseClick(result)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    },
                    transition: 'all 0.3s ease',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1F1F1F' }}>
                          {result.case_number}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <GavelIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            <strong>{result.court}</strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            • {result.date}
                          </Typography>
                        </Box>
                        {result.title && (
                          <Typography variant="subtitle1" sx={{ color: '#111', fontWeight: 600, mb: 0.5 }}>
                            {highlightText(result.title, result.matched_terms)}
                          </Typography>
                        )}
                        <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#444' }}>
                          {highlightText(result.summary, result.matched_terms)}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${Math.round(result.relevance_score * 100)}%`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: result.relevance_score > 0.8 ? 'rgba(76, 175, 80, 0.15)' : result.relevance_score > 0.6 ? 'rgba(231, 47, 13, 0.15)' : 'rgba(31, 31, 31, 0.08)',
                            color: result.relevance_score > 0.8 ? '#2e7d32' : result.relevance_score > 0.6 ? '#e72f0d' : '#1F1F1F',
                            border: result.relevance_score > 0.8 ? '1px solid rgba(76, 175, 80, 0.3)' : result.relevance_score > 0.6 ? '1px solid rgba(231, 47, 13, 0.3)' : '1px solid rgba(31, 31, 31, 0.15)'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          relevance
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Search sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#212121', mb: 1, fontWeight: 600 }} gutterBottom>
                Začněte vyhledáváním
              </Typography>
              <Typography variant="body1" sx={{ color: '#666' }}>
                Zadejte klíčová slova do vyhledávacího pole výše a najděte relevantní soudní rozhodnutí
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* AI Analysis */}
          {aiAnalysis && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>AI Analýza:</strong> {aiAnalysis}
              </Typography>
            </Alert>
          )}

          {/* Error Message */}
          {lawError && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {lawError}
            </Alert>
          )}

          {/* Law Results */}
          {lawLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : lawResults.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Nalezeno {lawResults.length} výsledků
              </Typography>
              {lawResults.map((result, index) => (
                <Card
                  key={index}
                  onClick={() => handleLawClick(result)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    },
                    transition: 'all 0.3s ease',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1F1F1F' }}>
                          {result.cislo_zakona}
                        </Typography>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#424242' }}>
                          {result.nazev}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <Chip
                            label={result.typ_dokumentu}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              backgroundColor: 'rgba(31, 31, 31, 0.08)',
                              color: '#1F1F1F',
                            }}
                          />
                        </Box>
                        <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#444' }}>
                          {result.preview}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={`${Math.round(result.relevance_score * 100)}%`}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: result.relevance_score > 0.8 ? 'rgba(76, 175, 80, 0.15)' : result.relevance_score > 0.6 ? 'rgba(231, 47, 13, 0.15)' : 'rgba(31, 31, 31, 0.08)',
                            color: result.relevance_score > 0.8 ? '#2e7d32' : result.relevance_score > 0.6 ? '#e72f0d' : '#1F1F1F',
                            border: result.relevance_score > 0.8 ? '1px solid rgba(76, 175, 80, 0.3)' : result.relevance_score > 0.6 ? '1px solid rgba(231, 47, 13, 0.3)' : '1px solid rgba(31, 31, 31, 0.15)'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          relevance
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <LawIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#212121', mb: 1, fontWeight: 600 }} gutterBottom>
                Začněte vyhledáváním
              </Typography>
              <Typography variant="body1" sx={{ color: '#666' }}>
                Zadejte číslo zákona, název nebo klíčová slova do vyhledávacího pole výše
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Box>

      {results.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={1} page={1} />
        </Box>
      )}
        </Box>
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={openDetail}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GavelIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Detail soudního rozhodnutí
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {loadingDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedCase ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Case Header */}
              <Box>
                <Typography variant="h5" gutterBottom sx={{ color: '#1F1F1F', fontWeight: 600 }}>
                  {selectedCase.case_number}
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#666' }}>
                  {highlightText(selectedCase.case_title, selectedCase.matched_terms)}
                </Typography>
              </Box>

              <Divider />

              {/* Case Info */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CourtIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Soud</Typography>
                    <Typography variant="body2">{selectedCase.court_name}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Datum rozhodnutí</Typography>
                    <Typography variant="body2">
                      {selectedCase.decision_date ?
                        new Date(selectedCase.decision_date).toLocaleDateString('cs-CZ') :
                        'Neuvedeno'}
                    </Typography>
                  </Box>
                </Box>

                {selectedCase.legal_area && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Právní oblast</Typography>
                      <Typography variant="body2">{selectedCase.legal_area}</Typography>
                    </Box>
                  </Box>
                )}

                {selectedCase.court_type && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GavelIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Typ soudu</Typography>
                      <Typography variant="body2">{selectedCase.court_type}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Summary */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Shrnutí rozhodnutí
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#333' }}>
                  {highlightText(selectedCase.case_summary, selectedCase.matched_terms)}
                </Typography>
              </Box>

              {/* Keywords */}
              {selectedCase.keywords && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Klíčová slova
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCase.keywords.split(',').map((keyword: string, index: number) => (
                      <Chip
                        key={index}
                        label={keyword.trim()}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: '#1F1F1F',
                          color: '#1F1F1F'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Full Detail from API if available */}
              {/* Full decision text */}
              {selectedCase.full_decision_text && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Úplný text rozhodnutí
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f9f9f9', maxHeight: 300, overflowY: 'auto' }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                      {selectedCase.full_decision_text}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedCase.fullDetail && (
                <>
                  {selectedCase.fullDetail.ai_analysis && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Analýza relevance
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          {selectedCase.fullDetail.ai_analysis}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {selectedCase.fullDetail.key_legal_points && selectedCase.fullDetail.key_legal_points.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Klíčové právní body
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        {selectedCase.fullDetail.key_legal_points.map((point: string, index: number) => (
                          <Box component="li" key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2">{point}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {selectedCase.fullDetail.related_cases && selectedCase.fullDetail.related_cases.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Související případy
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {selectedCase.fullDetail.related_cases.map((relatedCase: any) => (
                          <Paper key={relatedCase.id} sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F1F1F' }}>
                              {relatedCase.case_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {relatedCase.case_title}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Typography>Žádná data k zobrazení</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          {selectedCase && selectedCase.source_url && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(selectedCase.source_url, '_blank')}
              variant="outlined"
              sx={{
                color: '#1F1F1F',
                borderColor: '#1F1F1F',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  borderColor: '#1F1F1F'
                }
              }}
            >
              Otevřít originál
            </Button>
          )}
          <Button
            onClick={handleCloseDetail}
            variant="contained"
            sx={{
              backgroundColor: '#1F1F1F',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#e72f0d'
              }
            }}
          >
            Zavřít
          </Button>
        </DialogActions>
      </Dialog>

      {/* Law Detail Dialog */}
      <Dialog
        open={openLawDetail}
        onClose={handleCloseLawDetail}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            height: isMobile ? '100vh' : '90vh',
            maxHeight: isMobile ? '100vh' : '90vh',
          }
        }}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}>
          <IconButton onClick={handleCloseLawDetail} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {lawDetailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : selectedLaw ? (
            <LawDetail lawDetail={selectedLaw} onRelatedLawClick={handleRelatedLawClick} />
          ) : (
            <Typography>Žádná data k zobrazení</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ResearchPage;
